import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { preferences } from '@apexx/storage';

import {
	MAX_SHARE_FILES,
	PENDING_SHARE_FS_ROOT,
	PENDING_SHARE_PREFS_KEY,
} from './constants';
import {
	isPendingShareExpired,
	parsePendingShareJson,
	sanitizeShareFileName,
} from './pendingShareHelpers';
import type { PendingShare, PendingShareFile, ShareReceivedInput, SharedFileInput } from './types';

const memoryBlobs = new Map<string, Blob>();

function blobToBase64(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = reader.result;
			if (typeof result !== 'string') {
				reject(new Error('Failed to encode shared file.'));
				return;
			}

			const comma = result.indexOf(',');
			resolve(comma >= 0 ? result.slice(comma + 1) : result);
		};
		reader.onerror = () => reject(reader.error ?? new Error('Failed to read shared file.'));
		reader.readAsDataURL(blob);
	});
}

function base64ToBlob(base64: string, mimeType: string): Blob {
	const binary = atob(base64);
	const bytes = new Uint8Array(binary.length);

	for (let i = 0; i < binary.length; i += 1) {
		bytes[i] = binary.charCodeAt(i);
	}

	return new Blob([bytes], { type: mimeType || 'application/octet-stream' });
}

function toFilesystemUri(uri: string): string {
	if (uri.startsWith('file://')) {
		return uri;
	}

	if (uri.startsWith('/')) {
		return `file://${uri}`;
	}

	return uri;
}

function isNativeLocalPath(uri: string): boolean {
	return uri.startsWith('/') || uri.startsWith('file://');
}

async function readSharedBlob(file: SharedFileInput): Promise<Blob> {
	const memory = memoryBlobs.get(file.uri);
	if (memory) {
		return memory;
	}

	if (Capacitor.isNativePlatform() && isNativeLocalPath(file.uri)) {
		const { data } = await Filesystem.readFile({ path: toFilesystemUri(file.uri) });

		if (typeof data !== 'string') {
			throw new Error(`Unable to read shared file: ${file.name}`);
		}

		return base64ToBlob(data, file.mimeType || 'application/octet-stream');
	}

	const uri = toFilesystemUri(file.uri);
	const webPath = Capacitor.convertFileSrc(uri);
	const response = await fetch(webPath);

	if (!response.ok) {
		throw new Error(`Unable to read shared file: ${file.name}`);
	}

	return response.blob();
}

async function persistNativeFile(
	batchId: string,
	file: SharedFileInput,
	blob: Blob,
): Promise<PendingShareFile> {
	const safeName = sanitizeShareFileName(file.name);
	const relativePath = `${PENDING_SHARE_FS_ROOT}/${batchId}/${safeName}`;
	const base64 = await blobToBase64(blob);

	await Filesystem.writeFile({
		path: relativePath,
		data: base64,
		directory: Directory.Data,
		recursive: true,
	});

	const { uri } = await Filesystem.getUri({
		path: relativePath,
		directory: Directory.Data,
	});

	return {
		name: safeName,
		mimeType: file.mimeType || blob.type || 'application/octet-stream',
		localPath: uri,
		size: blob.size,
		relativePath,
	};
}

/**
 * Capgo / Share Extension hand off absolute paths (often App Group).
 * Prefer Filesystem with a full `file://` URI into Directory.Data.
 */
async function persistNativeSharedFile(
	batchId: string,
	file: SharedFileInput,
): Promise<PendingShareFile> {
	if (isNativeLocalPath(file.uri)) {
		const sourceUri = toFilesystemUri(file.uri);
		const { data } = await Filesystem.readFile({ path: sourceUri });

		if (typeof data !== 'string') {
			throw new Error(`Unable to read shared file: ${file.name}`);
		}

		const safeName = sanitizeShareFileName(file.name);
		const relativePath = `${PENDING_SHARE_FS_ROOT}/${batchId}/${safeName}`;

		await Filesystem.writeFile({
			path: relativePath,
			data,
			directory: Directory.Data,
			recursive: true,
		});

		let size = 0;
		try {
			const stat = await Filesystem.stat({
				path: relativePath,
				directory: Directory.Data,
			});
			size = stat.size;
		} catch {
			size = Math.floor((data.length * 3) / 4);
		}

		const { uri } = await Filesystem.getUri({
			path: relativePath,
			directory: Directory.Data,
		});

		return {
			name: safeName,
			mimeType: file.mimeType || 'application/octet-stream',
			localPath: uri,
			size,
			relativePath,
		};
	}

	const blob = await readSharedBlob(file);

	return persistNativeFile(batchId, file, blob);
}

async function persistWebFile(
	batchId: string,
	file: SharedFileInput,
	blob: Blob,
): Promise<PendingShareFile> {
	const safeName = sanitizeShareFileName(file.name);
	const key = `memory://${batchId}/${safeName}`;
	memoryBlobs.set(key, blob);

	return {
		name: safeName,
		mimeType: file.mimeType || blob.type || 'application/octet-stream',
		localPath: key,
		size: blob.size,
	};
}

async function removeRelativePath(relativePath: string): Promise<void> {
	try {
		await Filesystem.deleteFile({
			path: relativePath,
			directory: Directory.Data,
		});
	} catch {
		// Best-effort cleanup.
	}
}

async function clearPersistedFiles(share: PendingShare | null): Promise<void> {
	if (!share) {
		return;
	}

	for (const file of share.files) {
		if (file.localPath.startsWith('memory://')) {
			memoryBlobs.delete(file.localPath);
			continue;
		}

		if (file.relativePath && Capacitor.isNativePlatform()) {
			await removeRelativePath(file.relativePath);
		}
	}
}

export async function savePendingShare(share: PendingShare): Promise<void> {
	await preferences.setItem(PENDING_SHARE_PREFS_KEY, JSON.stringify(share));
}

export async function loadPendingShare(): Promise<PendingShare | null> {
	const value = await preferences.getItem(PENDING_SHARE_PREFS_KEY);
	const share = parsePendingShareJson(value);

	if (!share) {
		return null;
	}

	if (isPendingShareExpired(share)) {
		await clearPendingShare();
		return null;
	}

	return share;
}

export async function hasPendingShare(): Promise<boolean> {
	return (await loadPendingShare()) !== null;
}

export async function clearPendingShare(): Promise<void> {
	const value = await preferences.getItem(PENDING_SHARE_PREFS_KEY);
	const share = parsePendingShareJson(value);
	await preferences.removeItem(PENDING_SHARE_PREFS_KEY);
	await clearPersistedFiles(share);
}

/**
 * Persist an inbound OS share into durable app storage so login / navigation
 * can resume without relying on ephemeral content:// URIs.
 */
export async function saveShareReceivedEvent(event: ShareReceivedInput): Promise<PendingShare> {
	await clearPendingShare();

	const incomingFiles = event.files ?? [];
	const truncated = incomingFiles.length > MAX_SHARE_FILES;
	const filesToPersist = incomingFiles.slice(0, MAX_SHARE_FILES);
	const batchId = crypto.randomUUID();
	const persistedFiles: PendingShareFile[] = [];

	for (const file of filesToPersist) {
		const persisted = Capacitor.isNativePlatform()
			? await persistNativeSharedFile(batchId, file)
			: await persistWebFile(batchId, file, await readSharedBlob(file));
		persistedFiles.push(persisted);
	}

	const sharedText =
		(event.texts ?? []).map((text) => text.trim()).filter(Boolean).join('\n') || undefined;

	const share: PendingShare = {
		files: persistedFiles,
		sharedText,
		title: event.title?.trim() || undefined,
		receivedAt: new Date().toISOString(),
		truncated,
	};

	await savePendingShare(share);

	return share;
}

/** Load a pending file as a browser File for TUS upload. */
export async function loadPendingShareFileAsFile(file: PendingShareFile): Promise<File> {
	if (file.localPath.startsWith('memory://')) {
		const blob = memoryBlobs.get(file.localPath);
		if (!blob) {
			throw new Error(`Shared file missing from memory: ${file.name}`);
		}

		return new File([blob], file.name, { type: file.mimeType });
	}

	if (file.relativePath && Capacitor.isNativePlatform()) {
		const { data } = await Filesystem.readFile({
			path: file.relativePath,
			directory: Directory.Data,
		});

		if (typeof data !== 'string') {
			throw new Error(`Unable to read shared file: ${file.name}`);
		}

		const blob = base64ToBlob(data, file.mimeType);

		return new File([blob], file.name, { type: file.mimeType });
	}

	if (Capacitor.isNativePlatform() && isNativeLocalPath(file.localPath)) {
		const { data } = await Filesystem.readFile({ path: toFilesystemUri(file.localPath) });

		if (typeof data !== 'string') {
			throw new Error(`Unable to read shared file: ${file.name}`);
		}

		const blob = base64ToBlob(data, file.mimeType);

		return new File([blob], file.name, { type: file.mimeType });
	}

	const uri = toFilesystemUri(file.localPath);
	const webPath = Capacitor.convertFileSrc(uri);
	const response = await fetch(webPath);

	if (!response.ok) {
		throw new Error(`Unable to read shared file: ${file.name}`);
	}

	const blob = await response.blob();

	return new File([blob], file.name, {
		type: file.mimeType || blob.type || 'application/octet-stream',
	});
}

/** Test helper: seed an in-memory pending share without native I/O. */
export async function __setPendingShareForTests(share: PendingShare | null): Promise<void> {
	memoryBlobs.clear();

	if (!share) {
		await preferences.removeItem(PENDING_SHARE_PREFS_KEY);
		return;
	}

	await preferences.setItem(PENDING_SHARE_PREFS_KEY, JSON.stringify(share));
}
